import React from 'react';
import {
  Card, Row, Col, Input, Button, Select, DatePicker, Tag, Alert,
} from 'antd';
import {
  SafetyCertificateOutlined, SendOutlined, SearchOutlined,
  CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { initHandler, ajaxViewHandler, ajaxHandler, setConfig } from '../../store/actions';
import { Helmet } from 'react-helmet';
import Config from '../../Config';
import { toastSuccess, toastError } from '../../utils/toast';

const { RangePicker } = DatePicker;

const configClass = {
  title: 'Resend Jasa Raharja',
  apiUrl: {
    batch: 'transaksi/api/jasa/raharja/resend-batch/',
    nopol: 'transaksi/api/jasa/raharja/resend-by-nopol/',
    taskStatus: 'transaksi/api/jasa/raharja/task-status/',
    kabupaten: 'master/api/kabupaten/',
    upt: 'pegawai/api/upt/',
  },
};

const config = new Config();

const POLL_INTERVAL_MS = 3000;

/**
 * Kirim ulang / backfill data H2H Jasa Raharja berdasarkan Tagihan - BUKAN
 * dari tabel JasaRaharja (lihat be/thirdparty/jasa_raharja.py:_proses_kirim_jr).
 * Per transaksi otomatis: skip kalau row JasaRaharja-nya sudah Terkirim,
 * resend kalau Pending/Gagal, atau buat baris baru kalau belum ada row
 * sama sekali. Dua jalur:
 * - Massal (by tanggal paid_on) - async lewat Celery, dipoll via task-status.
 * - Per nomor polisi - sinkron, balasan langsung.
 * PENTING: ini benar-benar mengirim data ke webservice Jasa Raharja asli.
 */
class ResendJasaRaharja extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobile: window.innerWidth <= 768,

      // Batch (by tanggal)
      tglDari: '',
      tglSampai: '',
      statusTagihan: 3,
      loadingBatchSubmit: false,
      batchTaskId: null,
      batchPolling: false,
      batchResult: null,
      batchError: '',
      kabupatenDipilih: undefined,
      uptDipilih: undefined,
      listKabupaten: [],
      listUpt: [],
      loadingKabupaten: false,
      loadingUpt: false,

      // Per nomor polisi
      nomorPolisi: '',
      tglDariNopol: '',
      tglSampaiNopol: '',
      loadingNopolSubmit: false,
      nopolResult: null,
      nopolError: '',
    };
    this._pollTimer = null;
  }

  componentDidMount() {
    this.props.initHandler();
    this._handleResize = () => this.setState({ isMobile: window.innerWidth <= 768 });
    window.addEventListener('resize', this._handleResize);

    this.loadKabupaten();
    if (this.isPrivileged()) {
      this.loadUpt();
    } else {
      // staf biasa: kabupaten terkunci ke kabupaten dia sendiri - dikunci
      // di sini cuma buat UX (pre-select), enforcement sebenarnya tetap di
      // server (resend_batch, be/transaksi/views.py) karena kabupaten yang
      // dikirim dari sini bisa saja diabaikan server kalau beda - lihat
      // diskusi filter kabupaten/UPT resend massal 2026-10-14.
      const kabupatenSaya = this.props.kabupaten_id || undefined;
      this.setState({ kabupatenDipilih: kabupatenSaya, uptDipilih: this.props.upt_id || undefined });
      this.loadUpt(kabupatenSaya);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._handleResize);
    if (this._pollTimer) clearInterval(this._pollTimer);
  }

  isPrivileged() {
    return !!(this.props.is_superuser || this.props.level_user === 1 || this.props.level_user === 2);
  }

  _extractError = (resp, fallback) => {
    if (resp && typeof resp === 'object' && resp.error) return resp.error;
    if (typeof resp === 'string' && resp) return resp;
    return fallback;
  };

  loadKabupaten() {
    this.setState({ loadingKabupaten: true });
    this.props.ajaxViewHandler('get', configClass.apiUrl.kabupaten)
      .then(() => {
        const data = this.props.responseMsg;
        this.setState({
          listKabupaten: (this.props.isSuccess && (data.results || data)) || [],
          loadingKabupaten: false,
        });
      }).catch(() => this.setState({ loadingKabupaten: false }));
  }

  loadUpt(kabupatenId = '') {
    const url = kabupatenId
      ? configClass.apiUrl.upt + '?kabupaten=' + kabupatenId
      : configClass.apiUrl.upt;
    this.setState({ loadingUpt: true });
    this.props.ajaxViewHandler('get', url)
      .then(() => {
        const data = this.props.responseMsg;
        this.setState({
          listUpt: (this.props.isSuccess && (data.results || data)) || [],
          loadingUpt: false,
        });
      }).catch(() => this.setState({ loadingUpt: false }));
  }

  handleKabupatenChange = (val) => {
    this.setState({ kabupatenDipilih: val, uptDipilih: undefined, listUpt: [] });
    this.loadUpt(val || '');
  };

  // ── Batch by tanggal ───────────────────────────────────────────
  handleSubmitBatch = () => {
    const { tglDari, tglSampai, statusTagihan, kabupatenDipilih, uptDipilih } = this.state;
    if (!tglDari || !tglSampai) {
      toastError('Isi tanggal dari dan tanggal sampai terlebih dahulu');
      return;
    }
    this.setState({ loadingBatchSubmit: true, batchError: '', batchResult: null, batchTaskId: null });
    const payload = { tanggal_dari: tglDari, tanggal_sampai: tglSampai, status: statusTagihan };
    // kabupaten SELALU dikirim kalau terisi (termasuk staf biasa yang
    // terkunci) - server tetap jadi sumber kebenaran, nilai dari sini cuma
    // utk kenyamanan (gak perlu isi ulang), bukan satu-satunya pengaman.
    if (kabupatenDipilih) payload.kabupaten = kabupatenDipilih;
    if (uptDipilih) payload.upt = uptDipilih;
    this.props.ajaxHandler('post', configClass.apiUrl.batch, payload, false, true)
      .then(() => {
        if (this.props.isSuccess) {
          const data = this.props.responseMsg || {};
          this.setState({ batchTaskId: data.task_id, batchPolling: true });
          toastSuccess('Dikirim ke worker, menunggu hasil...', 3);
          this._startPolling(data.task_id);
        } else {
          this.setState({ batchError: this._extractError(this.props.responseMsg, 'Gagal mengirim permintaan') });
        }
        this.setState({ loadingBatchSubmit: false });
      }).catch(() => {
        this.setState({ loadingBatchSubmit: false, batchError: 'Gagal mengirim permintaan, coba lagi' });
      });
  };

  _startPolling = (taskId) => {
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = setInterval(() => {
      this.props.ajaxViewHandler('get', configClass.apiUrl.taskStatus + taskId + '/')
        .then(() => {
          if (!this.props.isSuccess) return;
          const data = this.props.responseMsg || {};
          if (data.state === 'completed') {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
            this.setState({ batchPolling: false, batchResult: data.result });
            toastSuccess('Proses selesai', 4);
          } else if (data.state === 'failed') {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
            this.setState({ batchPolling: false, batchError: data.error || 'Proses gagal di worker' });
          }
          // state processing - lanjut poll
        });
    }, POLL_INTERVAL_MS);
  };

  // ── Per nomor polisi ───────────────────────────────────────────
  handleSubmitNopol = () => {
    const { nomorPolisi, tglDariNopol, tglSampaiNopol } = this.state;
    const nopol = (nomorPolisi || '').trim();
    if (!nopol) {
      toastError('Isi nomor polisi terlebih dahulu');
      return;
    }
    if (Boolean(tglDariNopol) !== Boolean(tglSampaiNopol)) {
      toastError('Tanggal dari dan tanggal sampai harus diisi berdua atau dikosongkan berdua');
      return;
    }
    this.setState({ loadingNopolSubmit: true, nopolError: '', nopolResult: null });
    const payload = { nomor_polisi: nopol };
    if (tglDariNopol && tglSampaiNopol) {
      payload.tanggal_dari = tglDariNopol;
      payload.tanggal_sampai = tglSampaiNopol;
    }
    this.props.ajaxHandler('post', configClass.apiUrl.nopol, payload, false, true)
      .then(() => {
        if (this.props.isSuccess) {
          this.setState({ nopolResult: this.props.responseMsg });
          toastSuccess('Proses selesai', 4);
        } else {
          this.setState({ nopolError: this._extractError(this.props.responseMsg, 'Gagal memproses nomor polisi') });
        }
        this.setState({ loadingNopolSubmit: false });
      }).catch(() => {
        this.setState({ loadingNopolSubmit: false, nopolError: 'Gagal memproses, coba lagi' });
      });
  };

  // ── Render hasil (dipakai bersama kedua jalur) ──────────────────
  renderHasil(hasil) {
    if (!hasil) return null;
    const gagal = hasil.gagal || [];
    const stats = [
      { label: 'Total Kejaring', value: hasil.total, icon: <SearchOutlined />, color: '#0f2847' },
      { label: 'Diproses (Dikirim)', value: hasil.diproses, icon: <SendOutlined />, color: '#1d9d5c' },
      { label: 'Sudah Terkirim (Dilewati)', value: hasil.sudah_terkirim_dilewati, icon: <CheckCircleOutlined />, color: '#1d4ed8' },
      { label: 'Gagal', value: gagal.length, icon: <CloseCircleOutlined />, color: gagal.length ? '#cf1322' : '#999' },
    ];
    return (
      <div style={{ marginTop: 14 }}>
        <Row gutter={[10, 10]}>
          {stats.map(s => (
            <Col xs={12} md={6} key={s.label}>
              <div style={{
                borderRadius: 10, padding: '10px 14px', background: '#f6f8fc',
                border: '1px solid #e0e7f0', height: '100%',
              }}>
                <div style={{ fontSize: 11, color: '#888' }}>
                  <span style={{ marginRight: 6, color: s.color }}>{s.icon}</span>{s.label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color, marginTop: 2 }}>{s.value}</div>
              </div>
            </Col>
          ))}
        </Row>
        {gagal.length > 0 && (
          <div style={{
            marginTop: 10, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
            padding: '10px 14px', fontSize: 12, maxHeight: 160, overflowY: 'auto',
          }}>
            {gagal.map((g, i) => (
              <div key={i} style={{ marginBottom: 4 }}>
                <Tag color="red" style={{ borderRadius: 6 }}>{g.nomor_polisi || g.tagihan_id}</Tag>
                <span style={{ color: '#a8071a' }}>{g.alasan}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      isMobile, statusTagihan, loadingBatchSubmit,
      batchPolling, batchResult, batchError,
      nomorPolisi, loadingNopolSubmit,
      nopolResult, nopolError,
      kabupatenDipilih, uptDipilih, listKabupaten, listUpt,
      loadingKabupaten, loadingUpt,
    } = this.state;
    const privileged = this.isPrivileged();

    return (
      <div>
        <Helmet><title>{configClass.title + ' | ' + config.get_site_info().name}</title></Helmet>

        {/* Header */}
        <Card bodyStyle={{ padding: 0 }} style={{
          borderRadius: 14, overflow: 'hidden', marginBottom: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.10)', border: 'none',
        }}>
          <div style={{ background: 'linear-gradient(135deg, #0f2847 0%, #1a3a6b 100%)',
                        padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 50, height: 50, borderRadius: 12, background: 'rgba(251,191,36,0.15)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 22, color: '#fff' }}>
              <SafetyCertificateOutlined />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>{configClass.title}</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
                Kirim ulang data yang gagal/pending, atau isi data yang belum pernah terkirim ke Jasa Raharja
              </div>
            </div>
          </div>
        </Card>

        <Alert
          type="warning" showIcon style={{ borderRadius: 10, marginBottom: 16 }}
          message="Perhatian"
          description="Proses ini benar-benar mengirim data ke webservice Jasa Raharja asli, bukan simulasi. Gunakan rentang tanggal kecil atau satu nomor polisi untuk uji coba terlebih dahulu."
        />

        {/* Batch by tanggal */}
        <Card title={<span style={{ fontSize: 14, fontWeight: 700, color: '#0f2847' }}>
                <SendOutlined style={{ marginRight: 8 }} />Resend Massal (Berdasarkan Tanggal Pembayaran)</span>}
              style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: 'none', marginBottom: 16 }}
              bodyStyle={{ padding: isMobile ? 12 : '16px 20px' }}>
          <Row gutter={[12, 12]} align="bottom">
            <Col xs={24} sm={24} md={10}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Rentang Tanggal Pembayaran
              </div>
              <RangePicker size="large" style={{ width: '100%', borderRadius: 8 }} format="DD-MM-YYYY"
                placeholder={['Tanggal dari', 'Tanggal sampai']}
                onChange={(_, strings) => this.setState({ tglDari: strings[0], tglSampai: strings[1] })} />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Status Tagihan
              </div>
              <Select size="large" style={{ width: '100%', borderRadius: 8 }}
                value={statusTagihan}
                onChange={v => this.setState({ statusTagihan: v })}>
                <Select.Option value={3}>3 - Pembayaran (default)</Select.Option>
                <Select.Option value={2}>2 - Penetapan</Select.Option>
                <Select.Option value={1}>1 - Pendaftaran</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Button type="primary" block size="large" icon={<SendOutlined />}
                loading={loadingBatchSubmit || batchPolling} onClick={this.handleSubmitBatch}
                style={{ borderRadius: 8, fontWeight: 600, background: '#0f2847', borderColor: '#0f2847' }}>
                {batchPolling ? 'Sedang Diproses...' : 'Proses'}
              </Button>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Kabupaten/Kota {!privileged && <span style={{ color: '#888', fontWeight: 400 }}>(terkunci sesuai wilayah kamu)</span>}
              </div>
              <Select size="large" style={{ width: '100%', borderRadius: 8 }}
                allowClear={privileged} showSearch optionFilterProp="children"
                disabled={!privileged} loading={loadingKabupaten}
                placeholder="Semua Kabupaten/Kota" value={kabupatenDipilih}
                onChange={this.handleKabupatenChange}>
                {listKabupaten.map(k => (
                  <Select.Option key={k.id} value={k.id}>{k.name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                UPT (opsional)
              </div>
              <Select size="large" style={{ width: '100%', borderRadius: 8 }}
                allowClear showSearch optionFilterProp="children"
                loading={loadingUpt}
                placeholder="Semua UPT" value={uptDipilih}
                onChange={v => this.setState({ uptDipilih: v })}>
                {listUpt.map(u => (
                  <Select.Option key={u.id} value={u.id}>{u.nama}</Select.Option>
                ))}
              </Select>
            </Col>
          </Row>
          {batchPolling && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
              <ClockCircleOutlined style={{ marginRight: 6 }} />
              Diproses di background, mohon tunggu — halaman ini akan memperbarui hasil otomatis.
            </div>
          )}
          {batchError && (
            <div style={{ marginTop: 12, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
                          padding: '10px 14px', fontSize: 12, color: '#a8071a' }}>
              {batchError}
            </div>
          )}
          {this.renderHasil(batchResult)}
        </Card>

        {/* Per nomor polisi */}
        <Card title={<span style={{ fontSize: 14, fontWeight: 700, color: '#0f2847' }}>
                <SearchOutlined style={{ marginRight: 8 }} />Resend Per Nomor Polisi</span>}
              style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: 'none' }}
              bodyStyle={{ padding: isMobile ? 12 : '16px 20px' }}>
          <Row gutter={[12, 12]} align="bottom">
            <Col xs={24} sm={24} md={6}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Nomor Polisi
              </div>
              <Input size="large" placeholder="cth: KH1234AB" value={nomorPolisi}
                onChange={e => this.setState({ nomorPolisi: e.target.value })}
                onPressEnter={this.handleSubmitNopol}
                style={{ borderRadius: 8, fontWeight: 700, letterSpacing: 1 }} />
            </Col>
            <Col xs={24} sm={16} md={10}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Rentang Tanggal (opsional, kosongkan untuk semua histori)
              </div>
              <RangePicker size="large" style={{ width: '100%', borderRadius: 8 }} format="DD-MM-YYYY"
                placeholder={['Tanggal dari', 'Tanggal sampai']}
                onChange={(_, strings) => this.setState({ tglDariNopol: strings[0], tglSampaiNopol: strings[1] })} />
            </Col>
            <Col xs={24} sm={8} md={8}>
              <Button type="primary" block size="large" icon={<SearchOutlined />}
                loading={loadingNopolSubmit} onClick={this.handleSubmitNopol}
                style={{ borderRadius: 8, fontWeight: 600, background: '#0f2847', borderColor: '#0f2847' }}>
                Proses
              </Button>
            </Col>
          </Row>
          {nopolError && (
            <div style={{ marginTop: 12, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
                          padding: '10px 14px', fontSize: 12, color: '#a8071a' }}>
              {nopolError}
            </div>
          )}
          {this.renderHasil(nopolResult)}
        </Card>
      </div>
    );
  }
}

const mapStateToProps = state => state.configReducers;
export default connect(mapStateToProps, { initHandler, ajaxViewHandler, ajaxHandler, setConfig })(ResendJasaRaharja);
